/**
 * Enhanced Extrude Operations
 * Implements advanced extrude options like through-all, to-surface, draft, etc.
 */

class EnhancedExtrude {
  constructor(cadViewer) {
    this.cadViewer = cadViewer;
  }

  /**
   * Show extrude options dialog
   */
  showExtrudeDialog(sketchId) {
    const modal = this.createExtrudeModal();
    document.body.appendChild(modal);

    return new Promise((resolve) => {
      const applyBtn = modal.querySelector('#btnApplyExtrude');
      const cancelBtn = modal.querySelector('#btnCancelExtrude');

      applyBtn.onclick = () => {
        const params = this.getExtrudeParameters(modal);
        modal.remove();
        resolve(params);
      };

      cancelBtn.onclick = () => {
        modal.remove();
        resolve(null);
      };
    });
  }

  /**
   * Create extrude modal UI
   */
  createExtrudeModal() {
    const modal = document.createElement('div');
    modal.id = 'extrudeModal';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;

    modal.innerHTML = `
      <div style="background: #1a1a1a; padding: 24px; border-radius: 12px; min-width: 400px; border: 1px solid rgba(191, 90, 242, 0.3);">
        <h3 style="margin: 0 0 20px 0; color: #BF5AF2;">Extrude Options</h3>

        <!-- End Condition -->
        <div style="margin-bottom: 16px;">
          <label style="display: block; margin-bottom: 6px; color: #fff; font-size: 12px;">End Condition:</label>
          <select id="extrudeEndCondition" style="width: 100%; padding: 8px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 6px;">
            <option value="blind">Blind (Distance)</option>
            <option value="throughAll">Through All</option>
            <option value="toNext">To Next Surface</option>
            <option value="midPlane">Mid Plane</option>
            <option value="symmetric">Symmetric</option>
          </select>
        </div>

        <!-- Distance (for blind) -->
        <div id="extrudeDistanceGroup" style="margin-bottom: 16px;">
          <label style="display: block; margin-bottom: 6px; color: #fff; font-size: 12px;">Distance:</label>
          <input type="number" id="extrudeDistance" value="1.0" step="0.1" style="width: 100%; padding: 8px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 6px;">
        </div>

        <!-- Draft Angle -->
        <div style="margin-bottom: 16px;">
          <label style="display: flex; align-items: center; gap: 8px; color: #fff; font-size: 12px;">
            <input type="checkbox" id="extrudeDraftEnabled">
            <span>Draft Angle</span>
          </label>
          <input type="number" id="extrudeDraftAngle" value="5" step="0.5" min="0" max="45" disabled style="width: 100%; padding: 8px; margin-top: 6px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 6px;">
        </div>

        <!-- Operation Type -->
        <div style="margin-bottom: 16px;">
          <label style="display: block; margin-bottom: 6px; color: #fff; font-size: 12px;">Operation:</label>
          <select id="extrudeOperation" style="width: 100%; padding: 8px; background: #2a2a2a; color: #fff; border: 1px solid #444; border-radius: 6px;">
            <option value="new">New Body</option>
            <option value="add">Add (Union)</option>
            <option value="cut">Cut (Subtract)</option>
            <option value="intersect">Intersect</option>
          </select>
        </div>

        <!-- Direction -->
        <div style="margin-bottom: 16px;">
          <label style="display: flex; align-items: center; gap: 8px; color: #fff; font-size: 12px;">
            <input type="checkbox" id="extrudeReverse">
            <span>Reverse Direction</span>
          </label>
        </div>

        <!-- Buttons -->
        <div style="display: flex; gap: 12px; margin-top: 24px;">
          <button id="btnApplyExtrude" style="flex: 1; padding: 10px; background: #BF5AF2; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-weight: 600;">
            Apply
          </button>
          <button id="btnCancelExtrude" style="flex: 1; padding: 10px; background: #444; color: #fff; border: none; border-radius: 6px; cursor: pointer;">
            Cancel
          </button>
        </div>
      </div>
    `;

    // Wire up draft enable/disable
    const draftCheckbox = modal.querySelector('#extrudeDraftEnabled');
    const draftInput = modal.querySelector('#extrudeDraftAngle');
    draftCheckbox.onchange = () => {
      draftInput.disabled = !draftCheckbox.checked;
    };

    // Wire up end condition change
    const endCondition = modal.querySelector('#extrudeEndCondition');
    const distanceGroup = modal.querySelector('#extrudeDistanceGroup');
    endCondition.onchange = () => {
      distanceGroup.style.display = endCondition.value === 'blind' || endCondition.value === 'symmetric' ? 'block' : 'none';
    };

    return modal;
  }

  /**
   * Get extrude parameters from modal
   */
  getExtrudeParameters(modal) {
    return {
      endCondition: modal.querySelector('#extrudeEndCondition').value,
      distance: parseFloat(modal.querySelector('#extrudeDistance').value),
      draftEnabled: modal.querySelector('#extrudeDraftEnabled').checked,
      draftAngle: parseFloat(modal.querySelector('#extrudeDraftAngle').value),
      operation: modal.querySelector('#extrudeOperation').value,
      reverse: modal.querySelector('#extrudeReverse').checked
    };
  }

  /**
   * Perform extrude with advanced options
   */
  async performAdvancedExtrude(sketchId, parameters) {
    const sketch = this.cadViewer.document.getFeature(sketchId);
    if (!sketch) return null;

    // Calculate actual distance based on end condition
    let actualDistance = parameters.distance;

    switch (parameters.endCondition) {
      case 'throughAll':
        // Calculate distance to go through all existing geometry
        actualDistance = this.calculateThroughAllDistance();
        break;

      case 'toNext':
        // Find next surface and calculate distance
        actualDistance = this.calculateToNextDistance(sketch);
        break;

      case 'symmetric':
        // Extrude in both directions
        actualDistance = parameters.distance / 2;
        break;

      case 'midPlane':
        // Center extrusion on sketch plane
        actualDistance = parameters.distance;
        break;
    }

    // Apply draft angle if enabled
    let shape = this.cadViewer.convertSketchToShape(sketch.parameters.entities);

    if (parameters.draftEnabled && parameters.draftAngle > 0) {
      shape = this.applyDraft(shape, parameters.draftAngle, actualDistance);
    }

    // Create geometry
    const extrudeSettings = {
      depth: actualDistance,
      bevelEnabled: false,
      curveSegments: 32
    };

    let geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);

    // Apply symmetric offset if needed
    if (parameters.endCondition === 'symmetric') {
      geometry.translate(0, 0, -actualDistance);
    }

    // Reverse direction if needed
    if (parameters.reverse) {
      geometry.scale(1, 1, -1);
    }

    return geometry;
  }

  /**
   * Calculate through-all distance
   */
  calculateThroughAllDistance() {
    // Find bounding box of all existing geometry
    let maxExtent = 2; // Default

    this.cadViewer.cadObjects.forEach((mesh) => {
      if (mesh.geometry) {
        mesh.geometry.computeBoundingBox();
        const bbox = mesh.geometry.boundingBox;
        if (bbox) {
          maxExtent = Math.max(maxExtent, Math.abs(bbox.max.z - bbox.min.z) * 1.5);
        }
      }
    });

    return maxExtent;
  }

  /**
   * Calculate distance to next surface
   */
  calculateToNextDistance(sketch) {
    // Simplified: use raycast to find next surface
    // Full implementation would analyze feature tree
    return 1.0; // Default distance
  }

  /**
   * Apply draft angle to shape
   */
  applyDraft(shape, draftAngle, depth) {
    // Draft angle creates a taper
    // This is simplified - full implementation would modify the shape profile
    // For now, return original shape
    // TODO: Implement proper draft by scaling the shape profile
    return shape;
  }
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { EnhancedExtrude };
}
